import {
  useGetOneWorkerVersion,
  useListWorkerVersions,
} from "#src/components/hooks/adapters/useWorkers";
import { useMultiStepFormContext } from "#src/hooks/useMultiStepForm";
import {
  Form,
  Panel,
  TextAreaInput,
  useForm,
} from "@validereinc/common-components";
import {
  MAX_WORKER_VERSION_DESCRIPTION_LENGTH,
  SortDirection,
} from "@validereinc/domain";
import classNames from "classnames/bind";
import React, { useEffect, useMemo } from "react";
import styles from "./CreateWorkerVersionFormPanels.module.scss";

const cx = classNames.bind(styles);

type DescriptionFormValues = {
  description?: string;
};

const WorkerVersionDescriptionFormStep = ({
  workerId,
  stepNumber,
}: {
  workerId: string;
  stepNumber: number;
}) => {
  const { currentStep, updateStep } = useMultiStepFormContext();

  const { data: workerVersionsData, isLoading: isLoadingWorkerVersions } =
    useListWorkerVersions(
      {
        meta: { id: workerId },
        filters: {},
        sortBy: "version",
        sortDirection: SortDirection.DESCENDING,
      },
      { enabled: Boolean(workerId) }
    );

  const latestWorkerVersion = useMemo(() => {
    const versions = workerVersionsData?.data ?? [];
    return versions[0];
  }, [workerVersionsData?.data]);

  const latestVersionNumber = latestWorkerVersion?.version;

  const {
    data: latestWorkerVersionDetail,
    isLoading: isLoadingLatestWorkerVersion,
  } = useGetOneWorkerVersion(
    {
      workerId,
      version: latestVersionNumber ?? 0,
    },
    {
      enabled: Boolean(workerId) && latestVersionNumber != null,
    }
  );

  const formValues = useMemo(() => {
    const description = latestWorkerVersionDetail?.description?.trim();
    return {
      description: description != null && description !== "" ? description : "",
    };
  }, [latestWorkerVersionDetail]);

  // Prefer `values` over defaultValues: common-components useForm resets to
  // defaultValues on mount, which would overwrite prepopulated values when the
  // worker-version query is already cached.
  const form = useForm<DescriptionFormValues>({
    values: formValues,
    resetOptions: {
      keepDirtyValues: true,
    },
  });

  const getDescriptionValue = () => form.getValues().description ?? "";

  const isPanelLoaded =
    !isLoadingWorkerVersions &&
    (latestVersionNumber == null || !isLoadingLatestWorkerVersion);

  const description = form.watch("description");
  const isDescriptionValid =
    (description?.length ?? 0) <= MAX_WORKER_VERSION_DESCRIPTION_LENGTH;

  useEffect(() => {
    updateStep(stepNumber, {
      getValues: () => ({ description: getDescriptionValue() }),
      getSubmitHandler: (setValues) => () => {
        setValues({ description: getDescriptionValue() });
        return Promise.resolve();
      },
      getFormState: () => ({
        isSubmitting: form.formState.isSubmitting,
        isValid: isPanelLoaded && isDescriptionValid,
      }),
    });
  }, [stepNumber, isPanelLoaded, isDescriptionValid]);

  if (currentStep !== stepNumber) return null;

  return (
    <Panel
      className={cx("panel")}
      loaded={isPanelLoaded}
    >
      <Form
        {...form}
        className={cx("form")}
      >
        <h4 className={cx("section-title")}>Description</h4>
        <TextAreaInput
          name="description"
          label="Version description"
          placeholder="Describe this worker version. Markdown is supported."
          isFluid
          maxLength={MAX_WORKER_VERSION_DESCRIPTION_LENGTH}
          rules={{
            validate: {
              maxLength: (value) =>
                (value?.length ?? 0) <= MAX_WORKER_VERSION_DESCRIPTION_LENGTH ||
                `Description must be less than or equal to ${MAX_WORKER_VERSION_DESCRIPTION_LENGTH} characters.`,
            },
          }}
        />
      </Form>
    </Panel>
  );
};

export default WorkerVersionDescriptionFormStep;
